import React from "react";
import PrCard from "../PrCard";
import StatCard from "../StatCard";
import { heroPrs, heroStats } from "./heroFixtures";

/**
 * The product preview in the landing hero.
 *
 * Deliberately composed from the **real** PrCard and StatCard components with
 * fixture data, rather than a static image or a hand-built lookalike. Three
 * reasons:
 *
 *   1. It cannot drift. A design change to PrCard updates the hero for free;
 *      a screenshot would silently go stale and start misrepresenting the
 *      product.
 *   2. It stays crisp at any density and reflows responsively, which a PNG
 *      of a desktop dashboard does not.
 *   3. It ships no image weight.
 *
 * Presentation-only: pointer events are disabled so the preview can't be
 * mistaken for an interactive dashboard, and it's hidden from assistive tech
 * because the surrounding copy already conveys the meaning.
 */
export default function HeroMockup() {
  return (
    <div
      aria-hidden="true"
      className="relative select-none"
      // Presentational preview — not operable, so it must not sit in the tab
      // order or be announced.
      style={{ pointerEvents: "none" }}
    >
      {/* Accent wash behind the frame, per the design spec's hero treatment */}
      <div
        className="absolute -inset-8 rounded-3xl blur-3xl opacity-40"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 40%, rgba(0,212,170,0.18) 0%, rgba(0,153,255,0.08) 45%, transparent 75%)",
        }}
      />

      <div className="relative overflow-hidden rounded-2xl border border-rf-border bg-rf-bg-primary p-4 shadow-2xl md:p-5">
        {/* Chrome — signals "this is the app" without faking a browser window */}
        <div className="mb-4 flex items-center gap-2 border-b border-rf-border pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-gradient-to-br from-rf-accent to-rf-accent-blue text-[11px] font-bold text-white">
              A
            </div>
            <span className="text-sm font-semibold tracking-tight text-rf-text-primary">
              Review Dashboard
            </span>
          </div>
          <span className="ml-auto rounded-full bg-rf-accent/10 px-2 py-0.5 text-[10px] text-rf-accent">
            ● Connected
          </span>
        </div>

        {/* Same four cards, same labels, same subtitles and accents as
            Dashboard.js — a hero that renames things is no longer a preview of
            the product. Held at 2x2 rather than 1x4: in a half-width hero
            column, four across wraps "Avg Review Time" and "Avg Complexity"
            onto two lines and reads broken. */}
        <div className="mb-4 grid grid-cols-2 gap-3">
          <StatCard
            label="Awaiting Changes"
            value={heroStats.pending}
            sub="needs author action"
            accent="#ffa502"
          />
          <StatCard label="In Review" value={heroStats.inReview} accent="#0099ff" />
          <StatCard
            label="Avg Review Time"
            value={`${heroStats.avgTime}m`}
            sub="target: 30m"
            accent="#00d4aa"
          />
          <StatCard
            label="Avg Complexity"
            value={heroStats.avgComplexity}
            sub="/10.0"
            accent="#a855f7"
          />
        </div>

        {/* PR queue — the real card, ordered by priority as the API returns it.
            Clipped to one full card plus a glimpse of the next, so the preview
            reads as a window into a longer queue rather than the whole app. */}
        <div className="relative max-h-[300px] overflow-hidden">
          <div className="flex flex-col gap-3">
            {heroPrs.slice(0, 2).map((pr) => (
              <PrCard key={pr.pr_id} pr={pr} />
            ))}
          </div>
          {/* Sits above the clipped content so the cut reads as depth, not a
              crop. Fades to the frame's own background, which must therefore
              stay opaque. */}
          <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-rf-bg-primary via-rf-bg-primary/90 to-transparent" />
        </div>
      </div>
    </div>
  );
}
